import { useMemo, useState, type FormEvent } from 'react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import {
  periodTitle,
  WEEKDAY_LABEL,
} from '@/features/teacher/components/timetable/timetableCopy';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import type { AllocatableTeacher } from '@/types/allocation';
import type { TimetablePeriod, Weekday } from '@/types/timetable';
import type { AllottableSubject, SectionWeekEntry } from '@/types/timetableAdmin';
import { instituteTimetableApi } from '../api/timetable.api';
import { REPEATED_SUBJECT_WARNING, repeatsSubjectOnDay } from '../timetableWeek';

export interface PlaceLessonModalProps {
  /** The dated timetable the lesson goes in. */
  timetableUuid: string;
  sectionName: string;
  day: Weekday;
  period: TimetablePeriod;
  /** The subjects somebody is allotted here, with their teacher. */
  subjects: AllottableSubject[];
  /** Every subject the class studies this year, allotted or not. */
  classSubjects: { uuid: string; name: string }[];
  /** Who may be given a subject — offered for one nobody teaches here yet. */
  teachers: AllocatableTeacher[];
  /** `ALLOCATION_MANAGE`: without it only already-allotted subjects are offered. */
  canAllot: boolean;
  /** The section's lessons this week, to warn when the subject is already on this day. */
  entries: SectionWeekEntry[];
  onClose: () => void;
  onSaved: () => void;
}

/** A subject as the picker offers it: with its teacher, or with none yet. */
interface SubjectChoice {
  uuid: string;
  name: string;
  teacher: string | null;
}

/**
 * Puts one lesson in one cell of the recurring week.
 *
 * The teacher of a slot is read from the *allotment*, not stored beside it. A
 * subject already allotted here is placed as it is; one nobody teaches here
 * yet asks who should, and the server allots them and places the lesson in
 * the same save. That is what keeps a section without a class teacher — which
 * usually means without any allotment at all — from being a dead end.
 */
export function PlaceLessonModal({
  timetableUuid,
  sectionName,
  day,
  period,
  subjects,
  classSubjects,
  teachers,
  canAllot,
  entries,
  onClose,
  onSaved,
}: PlaceLessonModalProps) {
  const choices = useMemo<SubjectChoice[]>(() => {
    const allotted = subjects.map((subject) => ({
      uuid: subject.uuid,
      name: subject.name,
      teacher: subject.teacher,
    }));
    if (!canAllot) return allotted;

    const taken = new Set(allotted.map((subject) => subject.uuid));
    const vacant = classSubjects
      .filter((subject) => !taken.has(subject.uuid))
      .map((subject) => ({ uuid: subject.uuid, name: subject.name, teacher: null }));
    return [...allotted, ...vacant];
  }, [subjects, classSubjects, canAllot]);

  const [subjectUuid, setSubjectUuid] = useState(choices[0]?.uuid ?? '');
  const [teacherUuid, setTeacherUuid] = useState('');
  const [teacherError, setTeacherError] = useState<string | null>(null);
  const [clashError, setClashError] = useState<string | null>(null);
  const [roomName, setRoomName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const chosen = choices.find((subject) => subject.uuid === subjectUuid) ?? null;
  const needsTeacher = chosen !== null && chosen.teacher === null;
  const hasSubjects = choices.length > 0;
  const repeatsSubject = subjectUuid !== '' && repeatsSubjectOnDay(entries, subjectUuid, day);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (isSaving || !subjectUuid) return;
    if (needsTeacher && !teacherUuid) {
      setTeacherError(`Choose who teaches ${chosen.name} in ${sectionName}`);
      return;
    }

    setIsSaving(true);
    setClashError(null);
    try {
      await instituteTimetableApi.createEntry({
        timetableUuid,
        subjectUuid,
        periodUuid: period.uuid,
        dayOfWeek: day,
        roomName: roomName.trim() || undefined,
        teacherUuid: needsTeacher ? teacherUuid : undefined,
      });
      toast.success(
        `${chosen?.name ?? 'Lesson'} placed`,
        `${sectionName} · ${WEEKDAY_LABEL[day]}, ${periodTitle(period)}.`,
      );
      onSaved();
      onClose();
    } catch (caught) {
      const errorMessage =
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.';

      if (caught instanceof ApiRequestError && caught.message.includes('already takes')) {
        setClashError(caught.message);
      } else {
        toast.error('Could not place that lesson', errorMessage);
      }
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Place a lesson"
      description={`${sectionName} · ${WEEKDAY_LABEL[day]}, ${periodTitle(period)} (${period.startTime}–${period.endTime})`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            type="submit"
            form="place-lesson-form"
            isLoading={isSaving}
            disabled={!hasSubjects}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            {repeatsSubject ? 'Place anyway' : 'Place lesson'}
          </Button>
        </>
      }
    >
      <form id="place-lesson-form" onSubmit={save} className="flex flex-col gap-4 py-1">
        {hasSubjects ? (
          <>
            <Select
              label="Subject"
              options={choices.map((subject) => ({
                value: subject.uuid,
                label: subject.teacher
                  ? `${capitalizeWords(subject.name)} — ${subject.teacher}`
                  : `${capitalizeWords(subject.name)} — no teacher yet`,
              }))}
              value={subjectUuid}
              onChange={(event) => {
                setSubjectUuid(event.target.value);
                setTeacherError(null);
                setClashError(null);
              }}
              hint={
                chosen?.teacher
                  ? `${chosen.teacher} takes this subject in ${sectionName}.`
                  : 'Nobody teaches this here yet — choose who does below.'
              }
            />

            {needsTeacher && (
              <Select
                label="Teacher"
                placeholder="Choose a teacher"
                options={teachers.map((teacher) => ({
                  value: teacher.uuid,
                  label: teacher.designation
                    ? `${teacher.fullName} — ${teacher.designation}`
                    : teacher.fullName,
                }))}
                value={teacherUuid}
                onChange={(event) => {
                  setTeacherUuid(event.target.value);
                  setTeacherError(null);
                  setClashError(null);
                }}
                error={teacherError ?? undefined}
                hint={`They will be allotted ${chosen.name} in ${sectionName} for this year.`}
              />
            )}

            {repeatsSubject && (
              <Alert tone="warning" title="Already on this day">
                {REPEATED_SUBJECT_WARNING} You can still place it if that is intended.
              </Alert>
            )}

            {clashError && (
              <Alert tone="critical" title="Could not place that lesson">
                {clashError}
              </Alert>
            )}

            <Input
              label="Room (optional)"
              placeholder="e.g. Lab 2"
              maxLength={40}
              value={roomName}
              onChange={(event) => setRoomName(event.target.value)}
            />
          </>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {canAllot
              ? `${sectionName} has no subjects this year. Add its subjects on the Classes page, then place lessons here.`
              : `Nobody is allotted a subject in ${sectionName} yet, and allotting teachers is not part of your role. Ask your institute administrator to allot the teaching.`}
          </p>
        )}
      </form>
    </Modal>
  );
}
